import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, CheckSquare, ShoppingBasket, Calendar, Users, Wrench,
  UtensilsCrossed, FolderLock, MessagesSquare, PawPrint, Car, CreditCard,
  ChevronDown, ChevronUp, ExternalLink, ArrowRight, Activity, History, Layers,
} from 'lucide-react';
import { AppState, AppAction } from '@/types';
import { SubscriptionTier } from '@everyday-os/shared';
import { Changelog } from './Changelog';

interface ExploreProps {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

interface FeatureCard {
  id: string;
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  tier: SubscriptionTier | 'ADDON';
  tagline: string;
  bullets: string[];
  available: boolean;
  addonPrice?: string;
}

const TIER_META = {
  [SubscriptionTier.FREE]: { label: 'Free', className: 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400' },
  [SubscriptionTier.BASIC]: { label: 'Basic', className: 'bg-brand/10 text-brand dark:bg-brand/20' },
  [SubscriptionTier.PREMIUM]: { label: 'Premium', className: 'bg-[#ffde5a]/20 text-amber-700 dark:text-[#ffde5a]' },
  ADDON: { label: 'Add-On', className: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' },
} as const;

const TIER_ORDER: Record<SubscriptionTier, number> = {
  [SubscriptionTier.FREE]: 0,
  [SubscriptionTier.BASIC]: 1,
  [SubscriptionTier.PREMIUM]: 2,
};

function TierBadge({ tier }: { tier: SubscriptionTier | 'ADDON' }) {
  const meta = TIER_META[tier];
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-2xs font-black uppercase tracking-widest ${meta.className}`}>
      {meta.label}
    </span>
  );
}

function FeatureCardItem({ card, isAccessible }: { card: FeatureCard; isAccessible: boolean }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`bg-white dark:bg-slate-900 rounded-[1.5rem] border transition-all duration-200 ${
      expanded ? 'border-brand/40 shadow-lg shadow-brand/5' : 'border-slate-200 dark:border-slate-800'
    }`}>
      <div className="p-5 flex items-start gap-4">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
          isAccessible ? 'bg-brand/10' : 'bg-slate-100 dark:bg-slate-800'
        }`}>
          <card.icon className={`w-6 h-6 ${isAccessible ? 'text-brand' : 'text-slate-400'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-black text-slate-900 dark:text-white text-sm uppercase tracking-tight">{card.label}</h3>
            <TierBadge tier={card.tier} />
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{card.tagline}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isAccessible ? (
            <button
              onClick={() => navigate(card.path)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand text-white text-2xs font-black uppercase tracking-widest hover:bg-brand-hover transition-colors"
            >
              {t('explore.open', 'Open')} <ExternalLink className="w-3 h-3" />
            </button>
          ) : (
            <button
              onClick={() => setExpanded(e => !e)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 text-2xs font-black uppercase tracking-widest hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              {t('explore.see_how', 'See how it works')}
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          )}
        </div>
      </div>

      {/* Expandable detail panel — only shown for inaccessible features */}
      {!isAccessible && expanded && (
        <div className="px-5 pb-5 border-t border-slate-100 dark:border-slate-800 pt-4">
          <ul className="space-y-2 mb-5">
            {card.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-300">
                <span className="text-brand font-black mt-0.5">→</span>
                {b}
              </li>
            ))}
          </ul>
          {card.tier === 'ADDON' ? (
            <button
              onClick={() => navigate('/upgrade')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-900 text-xs font-black uppercase tracking-widest transition-colors"
            >
              {t('explore.add_to_plan', 'Add to my plan')} {card.addonPrice && `— ${card.addonPrice}`} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              onClick={() => navigate(`/upgrade?plan=${card.tier.toLowerCase()}`)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand hover:bg-brand-hover text-white text-xs font-black uppercase tracking-widest transition-colors"
            >
              {t('explore.upgrade_to', 'Upgrade to {{tier}}', { tier: TIER_META[card.tier as SubscriptionTier].label })} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export const Explore: React.FC<ExploreProps> = ({ state }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'features' | 'changelog'>('features');
  const currentHousehold = state.households?.find(h => h.id === state.currentHouseholdId);
  const householdTier = currentHousehold?.subscription?.tier ?? SubscriptionTier.FREE;

  const CORE_FEATURES: FeatureCard[] = [
    {
      id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard,
      tier: SubscriptionTier.FREE,
      tagline: 'A live overview of your household — tasks due, grocery levels, upcoming events, and your AI daily brief, all in one glance.',
      bullets: [],
      available: true,
    },
    {
      id: 'tasks', label: 'Tasks & Kanban', path: '/tasks', icon: CheckSquare,
      tier: SubscriptionTier.FREE,
      tagline: 'Drag-and-drop task boards for personal and shared chores, projects, and action items.',
      bullets: [],
      available: true,
    },
    {
      id: 'inventory', label: 'Grocery Lists', path: '/groceries', icon: ShoppingBasket,
      tier: SubscriptionTier.FREE,
      tagline: 'Smart grocery lists that learn from purchase history and predict what you\'ll run out of next.',
      bullets: [],
      available: true,
    },
    {
      id: 'calendar', label: 'Calendar', path: '/calendar', icon: Calendar,
      tier: SubscriptionTier.FREE,
      tagline: 'A shared household calendar with personal privacy controls, Google Calendar sync, and travel-time awareness.',
      bullets: [],
      available: true,
    },
    {
      id: 'contacts', label: 'Contacts & Services', path: '/contacts', icon: Users,
      tier: SubscriptionTier.BASIC,
      tagline: 'A household directory for service providers — plumbers, electricians, cleaners — with service history and ratings.',
      bullets: [
        'Store contacts with trade category, rate range, and service notes',
        'Link contacts to maintenance records and calendar appointments',
        'Filter by personal contacts vs. shared household service providers',
      ],
      available: TIER_ORDER[householdTier] >= TIER_ORDER[SubscriptionTier.BASIC],
    },
    {
      id: 'maintenance', label: 'Maintenance', path: '/maintenance', icon: Wrench,
      tier: SubscriptionTier.BASIC,
      tagline: 'Track home repairs, service schedules, appliances, and maintenance budgets in one place.',
      bullets: [
        'Log completed repairs with cost, contractor, and photos',
        'Set recurring service schedules with push notification reminders',
        'Appliance registry with warranty tracking and troubleshooting guidance',
      ],
      available: TIER_ORDER[householdTier] >= TIER_ORDER[SubscriptionTier.BASIC],
    },
    {
      id: 'meal-planner', label: 'Meal Planner', path: '/meal-planner', icon: UtensilsCrossed,
      tier: SubscriptionTier.BASIC,
      tagline: 'Plan weekly meals, save recipes, and auto-generate a grocery list from your meal plan.',
      bullets: [
        'Weekly drag-and-drop meal calendar for all household members',
        'Save and categorize your family\'s favourite recipes',
        'One tap adds all recipe ingredients to your grocery list',
      ],
      available: TIER_ORDER[householdTier] >= TIER_ORDER[SubscriptionTier.BASIC],
    },
    {
      id: 'documents', label: 'Document Vault', path: '/documents', icon: FolderLock,
      tier: SubscriptionTier.PREMIUM,
      tagline: 'A secure place for warranties, insurance policies, manuals, and important household files.',
      bullets: [
        'Upload and tag documents with AI-assisted categorisation',
        'Warranty expiry alerts sent automatically before they expire',
        'Share documents with household members or keep them private',
      ],
      available: TIER_ORDER[householdTier] >= TIER_ORDER[SubscriptionTier.PREMIUM],
    },
    {
      id: 'assistant', label: 'AI Assistant', path: '/assistant', icon: MessagesSquare,
      tier: SubscriptionTier.PREMIUM,
      tagline: 'A household-aware AI that knows your tasks, calendar, and home — so answers are always in context.',
      bullets: [
        'Ask anything about your household: "What\'s due this week?" "Who fixed the boiler last time?"',
        'AI embedded in every feature — task breakdown, event scheduling, grocery suggestions, maintenance predictions',
        'Voice-to-task: speak a task and it creates it with the right assignee, due date, and priority',
      ],
      available: TIER_ORDER[householdTier] >= TIER_ORDER[SubscriptionTier.PREMIUM],
    },
    {
      id: 'pets', label: 'Pet Management', path: '/pets', icon: PawPrint,
      tier: SubscriptionTier.BASIC,
      tagline: 'Profiles, care schedules, vet records, and medication tracking for every pet in your home.',
      bullets: [
        'Per-pet care routines (feeding, walks, grooming) that sync to your main task board',
        'Medical records, vaccination history, and upcoming vet appointment reminders',
        'Link your vet and groomer directly from the pet profile',
      ],
      available: TIER_ORDER[householdTier] >= TIER_ORDER[SubscriptionTier.BASIC],
    },
    {
      id: 'vehicles', label: 'Vehicle Tracking', path: '/vehicles', icon: Car,
      tier: SubscriptionTier.BASIC,
      tagline: 'Service history, mileage logs, insurance documents, and household vehicle booking in one place.',
      bullets: [
        'Track oil changes, tyre rotations, and any service with cost and contractor details',
        'Mileage log with predicted next-service milestones',
        'Book household vehicles by date/time with conflict detection',
      ],
      available: TIER_ORDER[householdTier] >= TIER_ORDER[SubscriptionTier.BASIC],
    },
    {
      id: 'health', label: 'Health', path: '/health', icon: Activity,
      tier: SubscriptionTier.BASIC,
      tagline: 'Track medications, upcoming medical appointments, and personal health notes — all in one private space.',
      bullets: [
        'Medication tracker with dose logging and refill threshold alerts',
        'Medical appointments pulled from your calendar automatically',
        'Private health notes visible only to you',
      ],
      available: TIER_ORDER[householdTier] >= TIER_ORDER[SubscriptionTier.BASIC],
    },
  ];

  const ADDON_FEATURES: FeatureCard[] = [];

  // Group core features by tier
  const freeTierCards = CORE_FEATURES.filter(f => f.tier === SubscriptionTier.FREE);
  const basicTierCards = CORE_FEATURES.filter(f => f.tier === SubscriptionTier.BASIC);
  const premiumTierCards = CORE_FEATURES.filter(f => f.tier === SubscriptionTier.PREMIUM);

  const TierSection = ({
    label,
    badge,
    cards,
    upgradeTier,
    upgradeLabel,
    ctaStyle,
  }: {
    label: string;
    badge: React.ReactNode;
    cards: FeatureCard[];
    upgradeTier?: string;
    upgradeLabel?: string;
    ctaStyle?: string;
  }) => {
    const allAccessible = cards.every(c => c.available);
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <span className="text-2xs font-black uppercase tracking-widest text-slate-400">{label}</span>
          {badge}
          {allAccessible && (
            <span className="text-2xs font-black uppercase tracking-widest text-emerald-500">{t('explore.active_plan', '— Active on your plan')}</span>
          )}
        </div>
        <div className="space-y-2">
          {cards.map(card => (
            <FeatureCardItem key={card.id} card={card} isAccessible={card.available} />
          ))}
        </div>
        {!allAccessible && upgradeTier && (
          <button
            onClick={() => navigate(`/upgrade?plan=${upgradeTier}`)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-colors ${ctaStyle}`}
          >
            {upgradeLabel} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-page-narrow mx-auto p-6 pb-24 space-y-6 animate-in fade-in duration-300">
      {/* Tab Bar */}
      <div className="flex gap-1 bg-slate-100 dark:bg-slate-800/60 rounded-2xl p-1">
        <button
          onClick={() => setActiveTab('features')}
          className={`flex items-center gap-2 flex-1 justify-center px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
            activeTab === 'features'
              ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm'
              : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
          }`}
        >
          <Layers className="w-3.5 h-3.5" />
          {t('explore.tab_features', 'Features')}
        </button>
        <button
          onClick={() => setActiveTab('changelog')}
          className={`flex items-center gap-2 flex-1 justify-center px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
            activeTab === 'changelog'
              ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm'
              : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
          }`}
        >
          <History className="w-3.5 h-3.5" />
          {t('explore.tab_changelog', 'Changelog')}
        </button>
      </div>

      {activeTab === 'features' && (
        <>
          <div>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-lg">
              {t('explore.tagline', 'Everything EverydayOS can do for your household. Open the features you already have, or see what else is waiting for you.')}
            </p>
          </div>

          {/* Core Features */}
          <div className="space-y-8">
            <TierSection
              label={t('explore.tier_free', 'Included Free')}
              badge={<TierBadge tier={SubscriptionTier.FREE} />}
              cards={freeTierCards}
            />
            <TierSection
              label={t('explore.tier_basic', 'Basic Plan')}
              badge={<TierBadge tier={SubscriptionTier.BASIC} />}
              cards={basicTierCards}
              upgradeTier="basic"
              upgradeLabel={t('explore.upgrade_basic', 'Upgrade to Basic — $4.99/mo')}
              ctaStyle="bg-brand text-white hover:bg-brand-hover"
            />
            <TierSection
              label={t('explore.tier_premium', 'Premium Plan')}
              badge={<TierBadge tier={SubscriptionTier.PREMIUM} />}
              cards={premiumTierCards}
              upgradeTier="premium"
              upgradeLabel={t('explore.upgrade_premium', 'Upgrade to Premium — $9.99/mo')}
              ctaStyle="bg-[#ffde5a] text-slate-900 hover:bg-yellow-300"
            />
          </div>

          {/* Add-Ons */}
          <div className="space-y-3">
            <div>
              <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">{t('explore.addons_title', 'Add-Ons')}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('explore.addons_desc', 'Available on any plan. Add only what you need.')}</p>
            </div>
            <div className="space-y-2">
              {ADDON_FEATURES.map(card => (
                <FeatureCardItem key={card.id} card={card} isAccessible={card.available} />
              ))}
            </div>
          </div>

          {/* Current plan reference */}
          <div className="bg-white dark:bg-slate-900 rounded-[1.5rem] border border-slate-200 dark:border-slate-800 p-5 flex items-center justify-between gap-4">
            <div>
              <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">{t('explore.current_plan', 'Your Current Plan')}</p>
              <p className="font-black text-slate-900 dark:text-white uppercase text-sm">
                {householdTier} {householdTier !== SubscriptionTier.FREE && '✓'}
              </p>
            </div>
            <button
              onClick={() => navigate('/upgrade')}
              className="px-5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 text-xs font-black uppercase tracking-widest hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              {t('explore.manage_subscription', 'Manage Subscription')}
            </button>
          </div>
        </>
      )}

      {activeTab === 'changelog' && <Changelog embedded />}
    </div>
  );
};
